import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, Youtube, Video, Building2 } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { PublishTarget } from '@utils/types/publishing/publish.ts';

function formatDuration(seconds?: number): string | null {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface Props {
  targets: PublishTarget[];
  isLoading: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  selectedId: string | null;
  agendaItemId: string | null;
  onPick: (target: PublishTarget, agendaItemId?: string) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

/**
 * Existing-target search + list for the publish dialog's choose-target
 * step. Covers YouTube videos (attach captions) and iBabs meetings
 * (agenda items pick a tracked clip).
 */
export default function StepChooseTargetExisting({
  targets,
  isLoading,
  query,
  onQueryChange,
  selectedId,
  agendaItemId,
  onPick,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [expanded, setExpanded] = useState<string | null>(null);
  const agendaRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // When a meeting is expanded and an agenda item is already chosen,
  // scroll it into view so the user doesn't have to hunt for the
  // selected radio.
  useEffect(() => {
    if (!expanded || !agendaItemId) return;
    const el = agendaRefs.current[`${expanded}:${agendaItemId}`];
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [expanded, agendaItemId]);

  return (
    <>
      <div
        className="flex items-center gap-2 rounded-lg border px-3 py-2"
        style={{
          borderColor: getColour('inputBorder'),
          background: getColour('firstLayer')
        }}
      >
        <Search
          className="h-4 w-4 shrink-0"
          aria-hidden="true"
          style={{ color: getColour('inactiveText') }}
        />
        <input
          type="search"
          value={query}
          onChange={e => onQueryChange(e.target.value)}
          placeholder={t('publish-choose-target-search-placeholder')}
          aria-label={t('publish-choose-target-search-placeholder')}
          className="w-full bg-transparent text-sm outline-none"
          style={{ color: getColour('normalText') }}
        />
      </div>

      <ul
        className="flex flex-col gap-2 max-h-[48vh] overflow-y-auto"
        aria-busy={isLoading}
      >
        {targets.map(target => {
          const selected = selectedId === target.id;
          return (
            <li key={`${target.kind}-${target.id}`}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  if (target.kind === 'ibabs_meeting') {
                    setExpanded(prev =>
                      prev === target.id ? null : target.id
                    );
                  } else {
                    onPick(target);
                  }
                }}
                className="flex w-full items-start gap-3 rounded-xl border p-3 text-left outline-none focus-visible:ring-2"
                style={{
                  borderColor: selected
                    ? getColour('activeText')
                    : getColour('hairline'),
                  background: getColour('secondLayer')
                }}
              >
                {target.kind === 'youtube_video' ? (
                  <div
                    className="relative flex h-14 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md"
                    style={{ background: getColour('thirdLayer') }}
                  >
                    {target.thumbnail_url ? (
                      <img
                        src={target.thumbnail_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Youtube
                        className="h-6 w-6"
                        aria-hidden="true"
                        style={{ color: getColour('inactiveText') }}
                      />
                    )}
                  </div>
                ) : target.kind === 'vimeo_video' ? (
                  <div
                    className="relative flex h-14 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md"
                    style={{ background: getColour('thirdLayer') }}
                  >
                    {target.thumbnail_url ? (
                      <img
                        src={target.thumbnail_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Video
                        className="h-6 w-6"
                        aria-hidden="true"
                        style={{ color: getColour('inactiveText') }}
                      />
                    )}
                  </div>
                ) : target.kind === 'ibabs_meeting' ? (
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    style={{ background: getColour('thirdLayer') }}
                  >
                    <Building2
                      className="h-5 w-5"
                      aria-hidden="true"
                      style={{ color: getColour('inactiveText') }}
                    />
                  </div>
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">
                    {target.title}
                  </p>
                  {target.kind === 'youtube_video' && (
                    <p
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {formatDuration(target.duration_seconds) ??
                        t('publish-youtube-video')}
                    </p>
                  )}
                  {target.kind === 'vimeo_video' && (
                    <p
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {formatDuration(target.duration_seconds) ??
                        t('publish-vimeo-video')}
                    </p>
                  )}
                  {target.kind === 'ibabs_meeting' && (
                    <p
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {target.starts_at ?? t('publish-ibabs-meeting')}
                    </p>
                  )}
                </div>
              </button>

              {target.kind === 'ibabs_meeting' &&
                expanded === target.id &&
                target.agenda_items.length > 0 && (
                  <ul
                    className="mt-1 ml-12 flex flex-col gap-1"
                    aria-label={t('publish-ibabs-agenda-items')}
                  >
                    {target.agenda_items.map(item => {
                      const picked = selected && agendaItemId === item.id;
                      const inputId = `publish-agenda-${target.id}-${item.id}`;
                      return (
                        <li key={item.id}>
                          <label
                            htmlFor={inputId}
                            className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm outline-none focus-within:ring-2"
                            style={{
                              borderColor: picked
                                ? getColour('activeText')
                                : getColour('hairline'),
                              background: getColour('firstLayer')
                            }}
                          >
                            <input
                              id={inputId}
                              ref={el => {
                                agendaRefs.current[`${target.id}:${item.id}`] =
                                  el;
                              }}
                              type="radio"
                              className="sr-only"
                              name={`publish-agenda-${target.id}`}
                              checked={picked}
                              onChange={() => onPick(target, item.id)}
                            />
                            <span className="truncate">{item.title}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => fetchNextPage?.()}
            disabled={isFetchingNextPage}
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm outline-none focus-visible:ring-2"
            style={{
              borderColor: getColour('hairline'),
              background: getColour('secondLayer'),
              color: getColour('normalText')
            }}
          >
            {isFetchingNextPage ? (
              <Loader2
                className="h-4 w-4 animate-spin"
                aria-hidden="true"
                style={{ color: getColour('inactiveText') }}
              />
            ) : null}
            {t('publish-choose-target-load-more')}
          </button>
        </div>
      )}
    </>
  );
}
